'use client';

import Link from "next/link";
import { LogOut, Menu, Settings, User } from 'lucide-react';
import { signOut } from "aws-amplify/auth";
import { useRouter, usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { Drawer } from "@/components/ui/drawer";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { useAuthStore } from "@/stores/auth-store";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react";
import { Button } from "@/components/common/Button";

export function Navbar() {
  const { isAuthenticated, user, isLoading, clearAuth, isAdmin } = useAuthStore();
  const router = useRouter();
  const pathname = usePathname();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const [isScrolling, setIsScrolling] = useState(false);
  const [isMobileAdminModalOpen, setIsMobileAdminModalOpen] = useState(false);

  // URL 해시 변경 감지: 해시가 변경되면 activeSection 즉시 업데이트
  useEffect(() => {
    if (pathname !== '/') {
      return;
    }

    const updateSectionFromHash = () => {
      const hash = window.location.hash.slice(1); // '#' 제거
      const validSections = ['intro', 'about', 'how', 'sample', 'reviews'];

      if (hash && validSections.includes(hash)) {
        setActiveSection(hash);

        // 해시에 해당하는 섹션으로 스크롤 (이미 스크롤 중이 아닐 때만)
        const element = document.getElementById(hash);
        if (element) {
          // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 스크롤
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }
    };

    // 초기 로드 시 해시 확인
    updateSectionFromHash();

    // 해시 변경 감지 (popstate 이벤트는 브라우저 뒤로가기/앞으로가기)
    window.addEventListener('hashchange', updateSectionFromHash);

    return () => {
      window.removeEventListener('hashchange', updateSectionFromHash);
    };
  }, [pathname]);

  // ScrollSpy: Intersection Observer API를 사용한 섹션 추적
  useEffect(() => {
    // 메인 페이지가 아니면 scrollSpy 비활성화
    if (pathname !== '/') {
      setActiveSection('');
      return;
    }

    const sections = ['intro', 'about', 'how', 'sample', 'reviews'];
    const sectionElements = sections
      .map((id) => {
        const element = document.getElementById(id);
        return element ? { id, element } : null;
      })
      .filter((item): item is { id: string; element: HTMLElement } => item !== null);

    if (sectionElements.length === 0) {
      return;
    }

    // 각 섹션의 intersection 상태를 추적
    const sectionStates = new Map<string, { isIntersecting: boolean; top: number }>();

    const updateActiveSection = () => {
      // 클릭으로 인한 스크롤 중일 때는 업데이트 건너뛰기
      if (isScrolling) {
        return;
      }

      // 현재 보이는 섹션들 중에서 선택
      const visibleSections = Array.from(sectionStates.entries())
        .filter(([, state]) => state.isIntersecting)
        .map(([id, state]) => ({ id, top: state.top }));

      if (visibleSections.length === 0) {
        return;
      }

      // 뷰포트 상단에 가장 가까운 섹션 선택
      // top 값이 작을수록 뷰포트 상단에 가까움
      const closestSection = visibleSections.reduce((prev, current) =>
        current.top < prev.top ? current : prev
      );

      setActiveSection(closestSection.id);
    };

    // Intersection Observer 생성
    // rootMargin: '-20% 0px -70% 0px' - 뷰포트 상단 20% 영역에서 감지
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionId = entry.target.id;
          sectionStates.set(sectionId, {
            isIntersecting: entry.isIntersecting,
            top: entry.boundingClientRect.top,
          });
        });
        updateActiveSection();
      },
      {
        rootMargin: '-20% 0px -70% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    // 각 섹션에 Observer 등록
    sectionElements.forEach(({ element }) => {
      observer.observe(element);
    });

    // 초기 상태 업데이트를 위한 스크롤 이벤트 (Observer가 초기 상태를 즉시 제공하지 않을 수 있음)
    const handleInitialScroll = () => {
      sectionElements.forEach(({ id, element }) => {
        const rect = element.getBoundingClientRect();
        const isIntersecting =
          rect.top < window.innerHeight * 0.8 && rect.bottom > window.innerHeight * 0.2;
        sectionStates.set(id, {
          isIntersecting,
          top: rect.top,
        });
      });
      updateActiveSection();
    };

    // 초기 실행
    handleInitialScroll();

    // 클린업
    return () => {
      sectionElements.forEach(({ element }) => {
        observer.unobserve(element);
      });
      observer.disconnect();
    };
  }, [pathname, isScrolling]);

  // 네비게이션 링크 클릭 핸들러
  const handleNavClick = (e: React.MouseEvent<HTMLButtonElement>, sectionId: string) => {
    e.preventDefault();
    setIsDrawerOpen(false);

    // 메인 페이지가 아니면 먼저 메인 페이지로 이동
    if (pathname !== '/') {
      // 해시 기반으로 activeSection 즉시 설정
      setActiveSection(sectionId);
      router.push(`/#${sectionId}`);
    } else {
      // 메인 페이지면 activeSection 즉시 업데이트 후 스크롤
      setActiveSection(sectionId);
      const element = document.getElementById(sectionId);
      if (element) {
        // 스크롤 시작 표시
        setIsScrolling(true);
        element.scrollIntoView({ behavior: 'smooth' });

        // 스크롤 완료 후 ScrollSpy 다시 활성화
        // smooth scroll은 보통 500-1000ms 정도 소요되므로 여유를 두고 1200ms 후 해제
        setTimeout(() => {
          setIsScrolling(false);
        }, 1200);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();

      // localStorage에서 토큰 제거
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');

      // Zustand store 초기화
      clearAuth();

      // Drawer 닫기
      setIsDrawerOpen(false);

      router.push('/');
    } catch (error) {
      console.error('로그아웃 실패:', error);
    }
  };

  const handleLinkClick = () => {
    setIsDrawerOpen(false);
  };

  // 모바일 감지 함수
  const isMobileDevice = (): boolean => {
    if (typeof window === 'undefined') {
      return false;
    }
    // 화면 크기 기반 감지 (lg 브레이크포인트: 1024px)
    return window.innerWidth < 1024;
  };

  // 관리자 메뉴 클릭 핸들러
  const handleAdminClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isMobileDevice()) {
      e.preventDefault();
      setIsMobileAdminModalOpen(true);
      setIsDrawerOpen(false);
    } else {
      handleLinkClick();
    }
  };

  // 메뉴 컨텐츠 (모바일 Drawer와 PC 헤더에서 재사용)
  const renderMenuContent = (isMobile = false) => {
    if (isLoading) {
      return (
        <div className={isMobile ? "flex flex-col gap-3" : "flex items-center gap-4"}>
          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
          <div className="h-9 w-20 bg-gray-200 rounded animate-pulse" />
        </div>
      );
    }

    if (isAuthenticated) {
      // 사용자 이름이 없거나 유효하지 않을 때 로딩 스켈레톤 표시
      const displayName = user?.name || user?.username;
      const isNameLoading = !displayName || displayName.trim() === '';

      return (
        <div className={isMobile ? "flex flex-col gap-3" : "flex items-center gap-4"}>
          {/* 사용자 이름 표시 */}
          {isNameLoading ? (
            <div className={`h-4 w-16 bg-gray-200 rounded animate-pulse ${isMobile ? 'py-2 border-b border-gray-100' : ''}`} />
          ) : (
            <span className={`text-sm font-medium text-gray-700 ${isMobile ? 'py-2 border-b border-gray-100' : ''}`}>
              {displayName}님
            </span>
          )}

          {/* 관리자 버튼 */}
          {isAdmin() && (
            isMobile ? (
              <Link
                href="/admin"
                onClick={handleAdminClick}
                className="flex items-center gap-2 w-full py-3 px-4 text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                aria-label="관리자 페이지"
              >
                <Settings className="w-5 h-5" />
                <span>관리자 페이지</span>
              </Link>
            ) : (
              <SimpleTooltip content="관리자 페이지" side="bottom">
                <Link
                  href="/admin"
                  onClick={handleAdminClick}
                  className="p-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors"
                  aria-label="관리자 페이지"
                >
                  <Settings className="w-5 h-5" />
                </Link>
              </SimpleTooltip>
            )
          )}

          {/* 마이페이지 버튼 */}
          {isMobile ? (
            <Link
              href="/mypage"
              onClick={handleLinkClick}
              className="flex items-center gap-2 w-full py-3 px-4 text-gray-700 hover:bg-gray-50 rounded-md transition-colors cursor-pointer"
              aria-label="마이페이지"
            >
              <User className="w-5 h-5" />
              <span>마이페이지</span>
            </Link>
          ) : (
            <SimpleTooltip content="마이페이지" side="bottom">
              <Link
                href="/mypage"
                className="p-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors cursor-pointer"
                aria-label="마이페이지"
              >
                <User className="w-5 h-5" />
              </Link>
            </SimpleTooltip>
          )}

          {/* 로그아웃 버튼 */}
          {isMobile ? (
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 w-full py-3 px-4 text-gray-700 hover:bg-gray-50 rounded-md transition-colors cursor-pointer"
              aria-label="로그아웃"
            >
              <LogOut className="w-5 h-5" />
              <span>로그아웃</span>
            </button>
          ) : (
            <SimpleTooltip content="로그아웃" side="bottom">
              <button
                type="button"
                onClick={handleLogout}
                className="p-2 text-gray-700 hover:text-red-600 hover:bg-gray-50 rounded-md transition-colors cursor-pointer"
                aria-label="로그아웃"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </SimpleTooltip>
          )}
        </div>
      );
    }

    return (
      <div className={isMobile ? "flex flex-col gap-3" : "flex items-center gap-4"}>
        <Link
          href="/login"
          onClick={isMobile ? handleLinkClick : undefined}
          className={`text-sm font-medium ${isMobile
            ? 'w-full py-3 px-4 text-gray-700 hover:bg-gray-50 rounded-md transition-colors'
            : 'px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors'
            }`}
        >
          로그인
        </Link>
        <Link
          href="/signup"
          onClick={isMobile ? handleLinkClick : undefined}
          className={`text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${isMobile ? 'w-full py-3 px-4' : 'px-4 py-2'
            }`}
        >
          회원가입
        </Link>
      </div>
    );
  };

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* 로고 */}
            <div className="flex-shrink-0">
              <Link href="/" className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-xl bg-blue-600 flex  items-center justify-center flex-shrink-0">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-label="고용가능성 진단 시스템 로고"
                  >
                    <title>고용가능성 진단 시스템 로고</title>
                    {/* 3개의 막대 그래프 (우상향) */}
                    <rect x="3" y="16" width="4" height="5" fill="white" rx="1" />
                    <rect x="10" y="11" width="4" height="10" fill="white" rx="1" />
                    <rect x="17" y="6" width="4" height="15" fill="white" rx="1" />
                    {/* 상승 화살표 */}
                    <path
                      d="M19 3L22 6M22 6L19 9M22 6H16"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-gray-900">고용가능성 진단 시스템</span>
                  <span className="text-xs text-gray-500 font-medium">NEXT CAREER LAB</span>
                </div>
              </Link>
            </div>

            {/* 네비게이션 메뉴 (PC에서만 표시, 가운데 정렬) */}
            <nav className="hidden lg:flex items-center justify-center gap-6 text-sm flex-1">
              <button
                type="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleNavClick(e, 'intro')}
                className={`transition-colors ${activeSection === 'intro'
                  ? 'text-blue-600 font-semibold'
                  : 'text-gray-700 hover:text-gray-900'
                  }`}
              >
                고용가능성 진단 소개
              </button>
              <button
                type="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleNavClick(e, 'how')}
                className={`transition-colors ${activeSection === 'how'
                  ? 'text-blue-600 font-semibold'
                  : 'text-gray-700 hover:text-gray-900'
                  } cursor-pointer`}
              >
                진단 절차
              </button>
              <button
                type="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleNavClick(e, 'sample')}
                className={`transition-colors ${activeSection === 'sample'
                  ? 'text-blue-600 font-semibold'
                  : 'text-gray-700 hover:text-gray-900'
                  } cursor-pointer`}
              >
                진단 결과 미리보기
              </button>
              <button
                type="button"
                onClick={(e) => handleNavClick(e, 'reviews')}
                className={`transition-colors ${activeSection === 'reviews'
                  ? 'text-blue-600 font-semibold'
                  : 'text-gray-700 hover:text-gray-900'
                  }`}
              >
                진단 후기
              </button>
              <Link
                href="/assessment"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                진단 시작
              </Link>
            </nav>

            {/* PC 메뉴 (lg 이상에서 표시) */}
            <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
              {renderMenuContent(false)}
            </div>

            {/* 모바일 햄버거 메뉴 버튼 (lg 미만에서 표시) */}
            <button
              type="button"
              onClick={() => setIsDrawerOpen(true)}
              className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
              aria-label="메뉴 열기"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* 모바일 Drawer */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="메뉴"
      >
        {/* 네비게이션 메뉴 */}
        <div className="flex flex-col gap-3 pb-4 mb-4 border-b border-gray-100">
          <button
            type="button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleNavClick(e, 'intro')}
            className={`w-full py-3 px-4 hover:bg-gray-50 rounded-md transition-colors text-sm cursor-pointer ${activeSection === 'intro'
              ? 'text-blue-600 font-semibold bg-blue-50'
              : 'text-gray-700'
              }`}
          >
            고용가능성 진단 소개
          </button>
          <button
            type="button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleNavClick(e, 'how')}
            className={`w-full py-3 px-4 hover:bg-gray-50 rounded-md transition-colors text-sm cursor-pointer ${activeSection === 'how'
              ? 'text-blue-600 font-semibold bg-blue-50'
              : 'text-gray-700'
              }`}
          >
            진단 절차
          </button>
          <button
            type="button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleNavClick(e, 'sample')}
            className={`w-full py-3 px-4 hover:bg-gray-50 rounded-md transition-colors text-sm cursor-pointer ${activeSection === 'sample'
              ? 'text-blue-600 font-semibold bg-blue-50'
              : 'text-gray-700'
              }`}
          >
            진단 결과 미리보기
          </button>
          <button
            type="button"
            onClick={(e) => handleNavClick(e, 'reviews')}
            className={`w-full py-3 px-4 hover:bg-gray-50 rounded-md transition-colors text-sm cursor-pointer ${activeSection === 'reviews'
              ? 'text-blue-600 font-semibold bg-blue-50'
              : 'text-gray-700'
              }`}
          >
            진단 후기
          </button>
          <Link
            href="/assessment"
            onClick={handleLinkClick}
            className="w-full py-3 px-4 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors text-sm font-medium text-center"
          >
            진단 시작
          </Link>
        </div>

        {/* 사용자 메뉴 */}
        {renderMenuContent(true)}
      </Drawer>

      {/* 모바일 관리자 접근 차단 모달 */}
      <Modal
        isOpen={isMobileAdminModalOpen}
        onOpenChange={setIsMobileAdminModalOpen}
        size="md"
        placement="center"
        scrollBehavior="inside"
        hideCloseButton
        classNames={{
          base: "bg-white rounded-lg shadow-xl",
          backdrop: "bg-black/50 backdrop-opacity-40",
          wrapper: "z-50 items-center justify-center",
        }}
      >
        <ModalContent className="bg-white rounded-lg shadow-xl p-0 max-h-[90vh] w-full flex flex-col">
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center justify-between gap-4 pb-4 border-b border-gray-200 px-6 pt-6 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">PC 접속 필요</h3>
              </ModalHeader>
              <ModalBody className="pt-4 pb-4 px-6">
                <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                  관리자 페이지는 PC에서만 이용 가능합니다.{'\n'}PC로 접속해주세요.
                </p>
              </ModalBody>
              <ModalFooter className="pt-4 border-t border-gray-200 px-6 pb-6 flex-shrink-0">
                <Button
                  variant="primary"
                  onPress={onClose}
                  className="w-full"
                >
                  확인
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
